import { fetchUtils, HttpError } from 'react-admin';
import type { AuthProvider } from './AuthProvider';
import {
  createReactAdminValidationError,
  getJsonApiErrorBody,
  getJsonApiErrorMessage,
  getJsonApiErrorStatus,
  JsonApiBulkOperationsResponse,
  JsonApiResource,
  JsonApiResourceLinkage,
} from './JsonApiTypes';

type JsonApiAtomicResult = JsonApiBulkOperationsResponse['atomic:results'][number];

interface BatchOperationRef {
  id: string;
  type: string;
  relationship?: string;
}

type BatchRelationshipData =
  JsonApiResourceLinkage<string> | JsonApiResourceLinkage<string>[] | null;

interface BatchAddOperation {
  op: 'add';
  data: JsonApiResource | Omit<JsonApiResource, 'id'>;
}

interface BatchUpdateDataOperation {
  op: 'update';
  data: Pick<JsonApiResource, 'id' | 'type'> &
    Partial<Pick<JsonApiResource, 'attributes' | 'relationships' | 'meta'>>;
}

interface BatchUpdateRelationshipOperation {
  op: 'update';
  ref: BatchOperationRef & { relationship: string };
  data: BatchRelationshipData;
}

interface BatchRemoveOperation {
  op: 'remove';
  ref: BatchOperationRef;
}

export type BatchOperation =
  | BatchAddOperation
  | BatchUpdateDataOperation
  | BatchUpdateRelationshipOperation
  | BatchRemoveOperation;

export interface BatchResult {
  'atomic:results': JsonApiAtomicResult[];
  meta?: JsonApiBulkOperationsResponse['meta'];
}

export type Batch = (operations: BatchOperation[]) => Promise<BatchResult>;

function getOperationResource(operation: BatchOperation): string {
  if ('ref' in operation) {
    return operation.ref.type;
  }

  return operation.data.type;
}

export default function batch(
  apiUrl: string,
  httpClient = fetchUtils.fetchJson,
  canAccess: typeof AuthProvider.canAccess = async () => true,
): Batch {
  const endpoint = new URL(apiUrl);
  endpoint.pathname = endpoint.pathname + '/operations';
  endpoint.pathname = endpoint.pathname.replace(/\/+/g, '/');

  return async (operations: BatchOperation[]): Promise<BatchResult> => {
    try {
      const resources = [...new Set(operations.map(getOperationResource))];

      for (const resource of resources) {
        if (!(await canAccess({ resource, action: 'write' }))) {
          throw new Error('Insufficient permissions');
        }
      }

      if (operations.length === 0) {
        return { 'atomic:results': [] };
      }

      const response = await httpClient(endpoint, {
        method: 'POST',
        body: JSON.stringify({ 'atomic:operations': operations }),
        headers: new Headers({
          'Content-Type': 'application/vnd.api+json; ext="https://jsonapi.org/ext/atomic"',
          Accept: 'application/vnd.api+json; ext="https://jsonapi.org/ext/atomic"',
        }),
      });

      return response.json as BatchResult;
    } catch (error) {
      const validationErrors = createReactAdminValidationError(error as HttpError);
      throw new HttpError(
        getJsonApiErrorMessage(error as HttpError, 'Error processing batch operation'),
        getJsonApiErrorStatus(error as HttpError),
        {
          ...validationErrors,
          apiResponse: getJsonApiErrorBody(error),
        },
      );
    }
  };
}
